// OperationReport47Component.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import { Loading } from "../../share/loading";
import "../../share/form.css";
import { Formik, Field, Form, FieldArray } from "formik";
import Swal from "sweetalert2";
import Select from "react-select";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

/* ===== constants & helpers ===== */
const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const YEARS = range(1963, new Date().getFullYear() + 8);

const N = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const fmtMoney = (n) =>
  Number(String(n ?? 0).replace(/,/g, "")).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const toThaiDateLabel = (v) => {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v; // already TH label
  const d = typeof v === "string" ? parseISO(v) : v;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "d")} ${monthName} ${format(be, "yyyy")}`;
};

const formatMonthYearBE = (date) => {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
};

/** พยายาม parse วันที่ไทยรูปแบบ "1 มกราคม 2567" -> Date (คริสต์ศักราช) */
const parseThaiDateLoose = (s) => {
  if (!s || typeof s !== "string") return null;
  const parts = s.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const [dStr, mStr, yStr] = parts;
  const d = parseInt(dStr.replace(/\D/g, ""), 10);
  const monthIdx = TH_MONTHS.findIndex((m) => m === mStr);
  const be = parseInt(yStr.replace(/\D/g, ""), 10);
  if (isNaN(d) || monthIdx < 0 || isNaN(be)) return null;
  const ad = be - 543;
  const dt = new Date(ad, monthIdx, d);
  return isNaN(dt) ? null : dt;
};

/* ===== PDF Thai font (optional) ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push([
    "addFonts",
    function () {
      this.addFileToVFS("Sarabun-Regular.ttf", regB64);
      this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
      this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    },
  ]);
  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport47Component() {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const [loading, setLoading] = useState(false);

  const [deptsData, setDeptsData] = useState([]); // [{value,label}]
  const [reportUsingMoney, setReportUsingMoney] = useState([]); // raw from API

  // filters (หน่วยงาน + เดือน/ปี)
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  const [reportMonthEnd, setReportMonthEnd] = useState("");
  const [reportMonthEndDate, setReportMonthEndDate] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // 10/20/50/100

  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
    GetReportUsingMoney();
  }, []);

  useEffect(() => {
    if (!reportMonth) {
      setReportMonthDate(null);
      return;
    }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  useEffect(() => {
    if (!reportMonthEnd) {
      setReportMonthEndDate(null);
      return;
    }
    const [y, m] = reportMonthEnd.split("-").map(Number);
    setReportMonthEndDate(new Date(y, m - 1, 1));
  }, [reportMonthEnd]);

  const GetReportUsingMoney = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/Support/reportUsingMoney", {
        method: "POST",
        headers: {
          token: user.token,
          "Content-type": "application/json",
        },
      });
      const resJson = await res.json();
      // console.log("resJson", resJson);
      const filterWaitApprove = (resJson?.dataenum || []).filter((item) => item.typeReport == "47");
      setReportUsingMoney(filterWaitApprove);
      setPage(1);
    } catch (e) {
      console.error(e);
      Swal.fire("ดึงข้อมูลไม่สำเร็จ", "", "error");
    } finally {
      setLoading(false);
    }
  };

  // apply filters (client-side)
  const filtered = useMemo(() => {
    let data = reportUsingMoney;

    if (deptIdFilter && deptIdFilter !== "all") {
      data = data.filter((it) => String(it?.deptId) === String(deptIdFilter));
    }

    if (reportMonth || reportMonthEnd) {
      data = data.filter(it => {
        const d = parseThaiDateLoose(it?.paymentDate);
        if (!d) return false;

        const ym = d.getFullYear() * 100 + (d.getMonth() + 1);

        const fromYM = reportMonth
          ? Number(reportMonth.replace("-", ""))
          : null;

        const toYM = reportMonthEnd
          ? Number(reportMonthEnd.replace("-", ""))
          : null;

        if (fromYM && ym < fromYM) return false;
        if (toYM && ym > toYM) return false;

        return true;
      });
    }


    return data;
  }, [reportUsingMoney, deptIdFilter, reportMonth, reportMonthEnd]);

  // ========= Group rows แบบเดียวกับหน้า 48 =========
  const groups = useMemo(() => {
    const map = new Map();

    for (const r of filtered) {
      const key = String(r.id || "");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }

    // sort ภายใน group
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) =>
        (a.lawOccupationName || "").localeCompare(
          b.lawOccupationName || "",
          "th"
        )
      );
    }

    return Array.from(map.values());
  }, [filtered]);

  // ========= flatten พร้อม rowSpan =========
  const flatRows = useMemo(() => {
    const out = [];
    for (const arr of groups) {
      arr.forEach((row, idx) => {
        out.push({
          ...row,
          _isFirst: idx === 0,
          _rowSpan: arr.length,
        });
      });
    }
    return out;
  }, [groups]);
  // console.log("flatRows", flatRows);

  // pagination derived
  const totalRows = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // formik init (ส่งรับทราบเฉพาะรายการที่อยู่ใน filtered ทั้งหมด)
  const dataInitialValuesforofficer = {
    reportSelectedAr: filtered.map((item) => ({
      resultKnowledgeId: item?.resultKnowledgeId,
      resultDsdId: item?.resultDsdId,
    })),
    deptId: deptIdFilter,
  };


  useEffect(() => {
    if (reportMonth && reportMonthEnd && reportMonthEnd < reportMonth) {
      Swal.fire("เดือนสิ้นสุดต้องมากกว่าหรือเท่ากับเดือนเริ่มต้น", "", "warning");
      setReportMonthEnd("");
      setReportMonthEndDate(null);
    }
  }, [reportMonth, reportMonthEnd]);
  /* ========================= Export: Excel ========================= */
  /* ========================= Export: Excel ========================= */
  const buildAoaAndMerges = () => {
    // ===== 2-row header (เหมือนบนหน้าจอ) =====
    const aoa = [
      [
        "ลำดับ",
        "หน่วยงาน",
        "สาขาอาชีพ",
        "สาขา",
        "จำนวนผู้เข้ารับการประเมิน (คน)",
        "จำนวนผู้ผ่านการประเมิน (คน)",
        "จำนวนเงิน ช่วยเหลือ หรืออุดหนุน", // colSpan 5
        "",
        "",
        "",
        "",
        "วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน",
      ],
      [
        "",
        "",
        "",
        "",
        "",
        "",
        "ค่าตอบแทนผู้ประเมิน (บาท)",
        "ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)",
        "ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)",
        "ค่าวัสดุใช้ในการประเมิน (บาท)",
        "รวม (บาท)",
        "",
      ],
    ];

    // ===== merges ของหัวตาราง =====
    const headerMerges = [
      // rowSpan 2 ของคอลัมน์ 0..5
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },
      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } },
      { s: { r: 0, c: 5 }, e: { r: 1, c: 5 } },

      // colSpan 5 ของ "จำนวนเงิน..." (c6..c10) แถวบน
      { s: { r: 0, c: 6 }, e: { r: 0, c: 10 } },

      // rowSpan 2 ของ "วันที่จ่ายเงิน..." (c11)
      { s: { r: 0, c: 11 }, e: { r: 1, c: 11 } },
    ];

    // ===== merges ของข้อมูล (เหมือนเดิม แต่ต้องเลื่อน rowIndex เพราะมี header 2 แถว) =====
    const merges = [];
    let rowIndex = 2; // data starts at row 3 (0-based = 2)

    flatRows.forEach((r, idx) => {
      const sumAmt =
        N(r.evaluatorExpense) +
        N(r.compensationExpense) +
        N(r.travellingExpense) +
        N(r.materialExpense);

      const row = [];

      row.push(idx + 1); // ลำดับ
      row.push(r.deptName || "-");
      row.push(r.lawOccupationName || "-");
      row.push(r.testOccupationName || "-");
      row.push(N(r.personTest));
      row.push(N(r.passEvaluation));

      if (r._isFirst) {
        // merge ค่าใช้จ่าย + วันที่จ่ายเงิน (คอลัมน์ 6..11)
        merges.push({ s: { r: rowIndex, c: 6 }, e: { r: rowIndex + r._rowSpan - 1, c: 6 } });
        merges.push({ s: { r: rowIndex, c: 7 }, e: { r: rowIndex + r._rowSpan - 1, c: 7 } });
        merges.push({ s: { r: rowIndex, c: 8 }, e: { r: rowIndex + r._rowSpan - 1, c: 8 } });
        merges.push({ s: { r: rowIndex, c: 9 }, e: { r: rowIndex + r._rowSpan - 1, c: 9 } });
        merges.push({ s: { r: rowIndex, c: 10 }, e: { r: rowIndex + r._rowSpan - 1, c: 10 } });
        merges.push({ s: { r: rowIndex, c: 11 }, e: { r: rowIndex + r._rowSpan - 1, c: 11 } });

        row.push(N(r.evaluatorExpense));
        row.push(N(r.compensationExpense));
        row.push(N(r.travellingExpense));
        row.push(N(r.materialExpense));
        row.push(sumAmt);
        row.push(r.paymentDate || "-");
      } else {
        // แถวในกลุ่มที่ไม่ใช่แถวแรก (ช่องที่ merge ปล่อยว่าง)
        row.push("");
        row.push("");
        row.push("");
        row.push("");
        row.push("");
        row.push("");
      }

      aoa.push(row);
      rowIndex++;
    });

    return { aoa, merges, headerMerges };
  };

  const handleExportExcel = () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }

    const { aoa, merges, headerMerges } = buildAoaAndMerges();

    // ===== SUMMARY (ไม่รวมซ้ำ) =====
    const firstRows = flatRows.filter((r) => r._isFirst);

    const totalEvaluator = firstRows.reduce((s, x) => s + N(x.evaluatorExpense), 0);
    const totalComp = firstRows.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = firstRows.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = firstRows.reduce((s, x) => s + N(x.materialExpense), 0);
    const totalSum = totalEvaluator + totalComp + totalTravel + totalMaterial;

    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passEvaluation), 0);

    aoa.push([
      "รวม",
      "", "", "",
      totalPerson,
      totalPass,
      totalEvaluator,
      totalComp,
      totalTravel,
      totalMaterial,
      totalSum,
      ""
    ]);

    const summaryRowIndex = aoa.length - 1; // 0-based

    // ===== recreate sheet AFTER push summary =====
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // 12 columns
    ws["!cols"] = [
      { wch: 8 },   // ลำดับ
      { wch: 28 },  // หน่วยงาน
      { wch: 24 },  // สาขาอาชีพ
      { wch: 20 },  // สาขา
      { wch: 24 },  // คนเข้า
      { wch: 24 },  // คนผ่าน
      { wch: 22 },  // ผู้ประเมิน
      { wch: 30 },  // นอกเวลา
      { wch: 28 },  // เดินทาง
      { wch: 22 },  // วัสดุ
      { wch: 18 },  // รวมเงิน
      { wch: 22 },  // วันที่จ่ายเงิน
    ];

    ws["!merges"] = [
      ...headerMerges,
      ...merges,
      // merge "รวม" ครอบ 4 คอลัมน์แรก (เหมือน tfoot)
      { s: { r: summaryRowIndex, c: 0 }, e: { r: summaryRowIndex, c: 3 } },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-7");

    XLSX.writeFile(
      wb,
      `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-7.xlsx`
    );
  };


  // const buildAoaAndMerges = () => {
  //   const aoa = [
  //     [
  //       "ลำดับ",
  //       "หน่วยงาน",
  //       "สาขาอาชีพ",
  //       "สาขา",
  //       "จำนวนผู้เข้ารับการประเมิน (คน)",
  //       "จำนวนผู้ผ่านการประเมิน (คน)",
  //       "ค่าตอบแทนผู้ประเมิน (บาท)",
  //       "ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)",
  //       "ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)",
  //       "ค่าวัสดุใช้ในการประเมิน (บาท)",
  //       "รวม (บาท)",
  //       "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
  //     ],
  //   ];

  //   const merges = [];
  //   let rowIndex = 1;

  //   flatRows.forEach((r, idx) => {
  //     const sumAmt =
  //       N(r.evaluatorExpense) +
  //       N(r.compensationExpense) +
  //       N(r.travellingExpense) +
  //       N(r.materialExpense);

  //     const row = [];

  //     // ● ลำดับตรงตามที่แสดงบนหน้าจอ
  //     row.push(idx + 1);

  //     // ● คอลัมน์ที่ไม่ merge
  //     row.push(r.deptName || "-");
  //     row.push(r.lawOccupationName || "-");
  //     row.push(r.testOccupationName || "-");
  //     row.push(N(r.personTest));
  //     row.push(N(r.passEvaluation));

  //     if (r._isFirst) {
  //       // ● Merge ใน Excel
  //       merges.push({ s: { r: rowIndex, c: 6 }, e: { r: rowIndex + r._rowSpan - 1, c: 6 } });
  //       merges.push({ s: { r: rowIndex, c: 7 }, e: { r: rowIndex + r._rowSpan - 1, c: 7 } });
  //       merges.push({ s: { r: rowIndex, c: 8 }, e: { r: rowIndex + r._rowSpan - 1, c: 8 } });
  //       merges.push({ s: { r: rowIndex, c: 9 }, e: { r: rowIndex + r._rowSpan - 1, c: 9 } });
  //       merges.push({ s: { r: rowIndex, c: 10 }, e: { r: rowIndex + r._rowSpan - 1, c: 10 } });
  //       merges.push({ s: { r: rowIndex, c: 11 }, e: { r: rowIndex + r._rowSpan - 1, c: 11 } });

  //       row.push(N(r.evaluatorExpense));
  //       row.push(N(r.compensationExpense));
  //       row.push(N(r.travellingExpense));
  //       row.push(N(r.materialExpense));
  //       row.push(sumAmt);
  //       row.push(r.paymentDate || "-");
  //     } else {
  //       // ● แถวในกลุ่มที่ไม่ใช่แถวแรก
  //       row.push("");
  //       row.push("");
  //       row.push("");
  //       row.push("");
  //       row.push("");
  //       row.push("");
  //     }

  //     aoa.push(row);
  //     rowIndex++;
  //   });

  //   return { aoa, merges };
  // };



  // const handleExportExcel = () => {
  //   if (!filtered.length) {
  //     Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
  //     return;
  //   }

  //   const { aoa, merges } = buildAoaAndMerges(filtered);

  //   // ===== SUMMARY =====
  //   // ===== SUMMARY (ไม่รวมซ้ำ) =====
  //   const firstRows = flatRows.filter(r => r._isFirst);

  //   const totalEvaluator = firstRows.reduce((s, x) => s + N(x.evaluatorExpense), 0);
  //   const totalComp = firstRows.reduce((s, x) => s + N(x.compensationExpense), 0);
  //   const totalTravel = firstRows.reduce((s, x) => s + N(x.travellingExpense), 0);
  //   const totalMaterial = firstRows.reduce((s, x) => s + N(x.materialExpense), 0);

  //   const totalSum = totalEvaluator + totalComp + totalTravel + totalMaterial;

  //   // คนเข้า/ผ่าน ใช้ filtered ได้ เพราะไม่ merge
  //   const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
  //   const totalPass = filtered.reduce((s, x) => s + N(x.passEvaluation), 0);

  //   aoa.push([
  //     "รวม",
  //     "", "", "",
  //     totalPerson,
  //     totalPass,
  //     totalEvaluator,
  //     totalComp,
  //     totalTravel,
  //     totalMaterial,
  //     totalSum,
  //     ""
  //   ]);

  //   const summaryRowIndex = aoa.length - 1;

  //   // ===== recreate sheet AFTER push summary =====
  //   const ws = XLSX.utils.aoa_to_sheet(aoa);

  //   ws["!cols"] = [
  //     { wch: 8 }, { wch: 28 }, { wch: 24 },
  //     { wch: 16 }, { wch: 18 },
  //     { wch: 24 }, { wch: 30 }, { wch: 30 }, { wch: 22 }, { wch: 16 },
  //     { wch: 26 }
  //   ];

  //   ws["!merges"] = [
  //     ...merges,
  //     {
  //       s: { r: summaryRowIndex, c: 0 },
  //       e: { r: summaryRowIndex, c: 3 }
  //     }
  //   ];

  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-7");
  //   XLSX.writeFile(
  //     wb,
  //     `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-7.xlsx`
  //   );
  // };


  /* ========================= Export: PDF ========================= */
  const handleExportPDF = async () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(
      `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-7`,
      40, 40
    );

    const head = [
      [
        { content: "ลำดับ", rowSpan: 2 },
        { content: "หน่วยงาน", rowSpan: 2 },       // ← เพิ่ม
        { content: "สาขาอาชีพ", rowSpan: 2 },
        { content: "สาขา", rowSpan: 2 },
        { content: "จำนวนผู้เข้ารับการประเมิน (คน)", rowSpan: 2 },
        { content: "จำนวนผู้ผ่านการประเมิน (คน)", rowSpan: 2 },

        { content: "จำนวนเงิน ช่วยเหลือ หรืออุดหนุน", colSpan: 5 },

        { content: "วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน", rowSpan: 2 },
      ],
      [
        { content: "ค่าตอบแทนผู้ประเมิน (บาท)" },
        { content: "ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)" },
        { content: "ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)" },
        { content: "ค่าวัสดุใช้ในการประเมิน (บาท)" },
        { content: "รวม (บาท)" },
      ],
    ];

    const body = [];

    flatRows.forEach((r, idx) => {
      const sumAmt =
        N(r.evaluatorExpense) +
        N(r.compensationExpense) +
        N(r.travellingExpense) +
        N(r.materialExpense);

      if (r._isFirst) {
        body.push([
          idx + 1,
          r.deptName || "-",
          r.lawOccupationName || "-",
          r.testOccupationName || "-",
          fmtMoney(r.personTest),
          fmtMoney(r.passEvaluation),
          fmtMoney(r.evaluatorExpense),
          fmtMoney(r.compensationExpense),
          fmtMoney(r.travellingExpense),
          fmtMoney(r.materialExpense),
          fmtMoney(sumAmt),
          r.paymentDate || "-",
        ]);
      } else {
        body.push([
          idx + 1,
          "",
          r.lawOccupationName || "-",
          r.testOccupationName || "-",
          fmtMoney(r.personTest),
          fmtMoney(r.passEvaluation),
          "",
          "",
          "",
          "",
          "",
          "",
        ]);
      }
    });



    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        1: { halign: "left" },   // deptName
        2: { halign: "left" },   // สาขาอาชีพ
        3: { halign: "left" },   // สาขา
        4: { halign: "right" },  // ผู้เข้ารับ
        5: { halign: "right" },  // ผู้ผ่าน
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "center" },
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    // summary
    const firstRows = flatRows.filter(r => r._isFirst);

    const totalEvaluator = firstRows.reduce((s, x) => s + N(x.evaluatorExpense), 0);
    const totalComp = firstRows.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = firstRows.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = firstRows.reduce((s, x) => s + N(x.materialExpense), 0);

    const totalSum = totalEvaluator + totalComp + totalTravel + totalMaterial;

    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passEvaluation), 0);

    const y = (doc.lastAutoTable?.finalY || 60) + 24;

    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(
      `รวม: ผู้เข้ารับฯ ${fmtMoney(totalPerson)} คน | ผ่านฯ ${fmtMoney(totalPass)} คน | ` +
      `ผู้ประเมิน ${fmtMoney(totalEvaluator)} | นอกเวลา ${fmtMoney(totalComp)} | เดินทาง ${fmtMoney(totalTravel)} | วัสดุ ${fmtMoney(totalMaterial)} | รวมเงิน ${fmtMoney(totalSum)} บาท`,
      40, y
    );

    doc.save(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-7.pdf`);
  };

  return (
    <Formik initialValues={dataInitialValuesforofficer} enableReinitialize>
      {(formik) => {
        return (
          <Container className="py-3">
            <Form>

              {/* Filters */}
              <Row className="g-3 align-items-end">
                <Col md={6}>
                  <Label>ชื่อหน่วยงาน</Label>
                  <Select
                    isSearchable
                    styles={{
                      control: (b) => ({ ...b, borderRadius: "20px" }),
                      singleValue: (b) => ({ ...b, color: "#0A66C2" }),
                    }}
                    options={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]}
                    value={
                      [{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
                        .find((o) => String(o.value) === String(deptIdFilter)) || null
                    }
                    onChange={(opt) => { setDeptIdFilter(opt?.value ?? "all"); setPage(1); }}
                    placeholder={<div className="input-color text-center">กรุณาเลือกหน่วยงาน</div>}
                    className="rounded-pill text-center input-color"
                  />
                </Col>

                <Col md={3}>
                  <Label>เดือน/ปี ที่จ่ายเงิน (ตั้งแต่)</Label>
                  <DatePicker
                    selected={reportMonthDate}
                    onChange={(date) => {
                      if (date instanceof Date && !isNaN(date)) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        setReportMonth(`${y}-${m}`);
                        setReportMonthDate(new Date(y, Number(m) - 1, 1));
                      } else {
                        setReportMonth("");
                        setReportMonthDate(null);
                      }
                      setPage(1);
                    }}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    showFullMonthYearPicker
                    locale="th-buddhist"
                    placeholderText="เลือกเดือน/ปี (พ.ศ.)"
                    value={formatMonthYearBE(reportMonthDate)}
                    renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                      <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <select className="form-select form-select-sm" style={{ width: 140 }} value={TH_MONTHS[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(TH_MONTHS.indexOf(value))}>
                            {TH_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                            {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                          </select>
                        </div>
                        <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                      </div>
                    )}
                    customInput={
                      <input
                        className="form-control rounded-pill text-center input-color"
                        readOnly
                        value={formatMonthYearBE(reportMonthDate)}
                        placeholder="เลือกเดือน/ปี (พ.ศ.)"
                      />
                    }
                  />
                </Col>
                <Col md={3}>
                  <Label>เดือน/ปี ที่จ่ายเงิน (ถึง)</Label>
                  <DatePicker
                    selected={reportMonthEndDate}
                    onChange={(date) => {
                      if (date instanceof Date && !isNaN(date)) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        setReportMonthEnd(`${y}-${m}`);
                        setReportMonthEndDate(new Date(y, Number(m) - 1, 1));
                      } else {
                        setReportMonthEnd("");
                        setReportMonthEndDate(null);
                      }
                      setPage(1);
                    }}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    showFullMonthYearPicker
                    locale="th-buddhist"
                    placeholderText="เลือกเดือน/ปี (พ.ศ.)"
                    value={formatMonthYearBE(reportMonthEndDate)}
                    renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                      <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <select className="form-select form-select-sm" style={{ width: 140 }} value={TH_MONTHS[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(TH_MONTHS.indexOf(value))}>
                            {TH_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                            {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                          </select>
                        </div>
                        <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                      </div>
                    )}
                    customInput={
                      <input
                        className="form-control rounded-pill text-center input-color"
                        readOnly
                        value={formatMonthYearBE(reportMonthEndDate)}
                        placeholder="เลือกเดือน/ปี (พ.ศ.)"
                      />
                    }
                  />
                </Col>

                <Col md="auto" className="pt-2 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
                  <button type="button" className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
                </Col>
              </Row>

              <div className="mt-3">
                <Table bordered responsive>
                  <thead className="table-light">
                    <tr>
                      <th rowSpan="2">ลำดับ</th>
                      <th rowSpan="2">หน่วยงาน</th>     {/* ← เพิ่ม */}
                      <th rowSpan="2">สาขาอาชีพ</th>
                      <th rowSpan="2">สาขา</th>
                      <th rowSpan="2">จำนวนผู้เข้ารับการประเมิน (คน)</th>
                      <th rowSpan="2">จำนวนผู้ผ่านการประเมิน (คน)</th>
                      <th colSpan="5">จำนวนเงิน ช่วยเหลือ หรืออุดหนุน</th>
                      <th rowSpan="2">วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน</th>
                    </tr>
                    <tr>
                      <th>ค่าตอบแทนผู้ประเมิน (บาท)</th>
                      <th>ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)</th>
                      <th>ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)</th>
                      <th>ค่าวัสดุใช้ในการประเมิน (บาท)</th>
                      <th>รวม (บาท)</th>
                    </tr>
                  </thead>

                  {!loading ? (
                    <FieldArray name="datatable">
                      {() => {
                        return (
                          <tbody>
                            {flatRows.length === 0 && (
                              <tr><td className="text-center" colSpan="12">ไม่มีข้อมูล</td></tr>
                            )}

                            {flatRows.map((item, idx) => {
                              const sumAmt =
                                N(item.evaluatorExpense) +
                                N(item.compensationExpense) +
                                N(item.travellingExpense) +
                                N(item.materialExpense);

                              return (
                                <tr key={idx}>
                                  {/* ลำดับ */}
                                  <td className="text-center">{idx + 1}</td>

                                  {/* 4 คอลัมน์ที่ไม่ merge */}
                                  <td className="text-center">{item.deptName || "-"}</td>
                                  <td className="text-center">{item.lawOccupationName || "-"}</td>
                                  <td className="text-center">{item.testOccupationName || "-"}</td>
                                  <td className="text-center">{fmtMoney(item.personTest)}</td>
                                  <td className="text-center">{fmtMoney(item.passEvaluation)}</td>

                                  {/* คอลัมน์ merge: แสดงเฉพาะแถวแรกของกลุ่ม */}
                                  {item._isFirst && (
                                    <>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {fmtMoney(item.evaluatorExpense)}
                                      </td>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {fmtMoney(item.compensationExpense)}
                                      </td>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {fmtMoney(item.travellingExpense)}
                                      </td>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {fmtMoney(item.materialExpense)}
                                      </td>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {fmtMoney(sumAmt)}
                                      </td>
                                      <td rowSpan={item._rowSpan} className="text-center">
                                        {item.paymentDate || "-"}
                                      </td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>

                        );
                      }}
                    </FieldArray>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan="11">
                          <Loading />
                        </td>
                      </tr>
                    </tbody>
                  )}
                  <tfoot className="table-light">
                    <tr>
                      <th colSpan="4" className="text-center">รวม</th>

                      {/* รวมจำนวนคน */}
                      <th className="text-center">
                        {fmtMoney(filtered.reduce((s, x) => s + N(x.personTest), 0))}
                      </th>
                      <th className="text-center">
                        {fmtMoney(filtered.reduce((s, x) => s + N(x.passEvaluation), 0))}
                      </th>

                      {/* ==== รวมเงิน แบบไม่ซ้ำ id ==== */}
                      {(() => {
                        const firstRows = flatRows.filter((r) => r._isFirst);

                        const totalEvaluator = firstRows.reduce(
                          (s, x) => s + N(x.evaluatorExpense),
                          0
                        );
                        const totalComp = firstRows.reduce(
                          (s, x) => s + N(x.compensationExpense),
                          0
                        );
                        const totalTravel = firstRows.reduce(
                          (s, x) => s + N(x.travellingExpense),
                          0
                        );
                        const totalMaterial = firstRows.reduce(
                          (s, x) => s + N(x.materialExpense),
                          0
                        );
                        const totalSum =
                          totalEvaluator + totalComp + totalTravel + totalMaterial;

                        return (
                          <>
                            <th className="text-center">{fmtMoney(totalEvaluator)}</th>
                            <th className="text-center">{fmtMoney(totalComp)}</th>
                            <th className="text-center">{fmtMoney(totalTravel)}</th>
                            <th className="text-center">{fmtMoney(totalMaterial)}</th>
                            <th className="text-center">{fmtMoney(totalSum)}</th>
                          </>
                        );
                      })()}

                      <th className="text-center">-</th>
                    </tr>
                  </tfoot>


                </Table>
              </div>


            </Form>
          </Container>
        );
      }}
    </Formik>
  );
}
